import "../styles/Sidebar.css";
import { useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import {
  FaChartBar,
  FaChartLine,
  FaFileAlt,
  FaSignOutAlt,
  FaUnlink,
  FaStore,
  FaPlus,
  FaListUl,
  FaCreditCard,
  FaUser,
  FaUserEdit,
  FaUserPlus,
  FaCogs,
  FaKey,
  FaChevronDown,
  FaChevronUp,
  FaDesktop,
  FaWarehouse,
} from "react-icons/fa";
import {
  FaMoneyBillWave,
  FaCheckCircle,
  FaClock,
  FaExclamationTriangle,
  FaTimesCircle,
  FaBan,
  FaCalendarDay,
  FaCity,
  FaMapMarkedAlt,
  FaTags,
} from "react-icons/fa";
import { MdDashboard, MdReceipt } from "react-icons/md";
import logo from "../resources/cheeziouslogo.png";
import mindscraft from "../resources/mindscraft-logo.png";
import { useAuth } from "../context/AuthContext";
import { clearUserData, getUserData } from "../utils/authStorage";

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const userData = getUserData();
  const user = userData?.user || userData || {};
  const userType = user.user_type;
  const roles = Array.isArray(user.roles) ? user.roles.flat() : [];

  const [expandedMenus, setExpandedMenus] = useState({});

  const handleLogout = () => {
    if (window.confirm("Are you sure you want to logout?")) {
      localStorage.removeItem("token");
      clearUserData();
      logout();
      navigate("/");
    }
  };

  const toggleMenu = (menuId) => {
    setExpandedMenus((prev) => ({
      ...prev,
      [menuId]: !prev[menuId],
    }));
  };

  const getVisibleMenuItems = () => {
    if (userType === "admin") {
      return [
        "dashboard",
        "reporting",
        "merchant",
        "terminal",
        "unbind",
        "hardpos",
        "inventory",
        "operations",
        "users",
      ];
    }
    if (userType === "subordinate") {
      if (roles.includes("view-only")) {
        return [
          "dashboard",
          "reporting",
          "merchant",
          "terminal",
          "hardpos",
          "inventory",
          "unbind",
          "operations",
        ];
      }
      if (roles.length === 1 && roles.includes("merchant_onboarding")) {
        return ["dashboard", "reporting", "merchant", "unbind"];
      }
      if (roles.length === 1 && roles.includes("terminal_creation")) {
        return ["dashboard", "reporting", "terminal", "unbind"];
      }
      if (
        roles.includes("merchant_onboarding") &&
        roles.includes("terminal_creation")
      ) {
        return [
          "dashboard",
          "reporting",
          "merchant",
          "terminal",
          "hardpos",
          "inventory",
          "unbind",
          "operations",
        ];
      }
    }
    return [];
  };

  const visibleItems = getVisibleMenuItems();

  /**
   * Exact active check: matches both pathname AND search query.
   * This prevents e.g. "reporting?type=merchants" from highlighting
   * when you're on "reporting?type=transactions".
   */
  const isExactActive = (path) => {
    const [pathname, queryString] = path.split("?");
    const pathMatches =
      location.pathname === pathname || location.pathname.endsWith(pathname);

    if (!queryString) return pathMatches;

    // Parse both the link's params and the current URL's params
    const linkParams = new URLSearchParams(queryString);
    const currentParams = new URLSearchParams(location.search);

    // Every param in the link must match the current URL
    for (const [key, value] of linkParams.entries()) {
      if (currentParams.get(key) !== value) return false;
    }

    return pathMatches;
  };

  /**
   * For top-level items without submenus, use a simple path check.
   */
  const isPathActive = (path) => location.pathname.includes(path);

  const sections = [
    {
      id: "main",
      label: null,
      items: [
        {
          id: "dashboard",
          label: "Dashboard",
          icon: <MdDashboard />,
          path: "/dashboard",
        },
      ],
    },
    {
      id: "operations",
      label: "OPERATIONS",
      items: [
        {
          id: "merchant",
          label: "Merchant",
          icon: <FaStore />,
          submenu: [
            {
              label: "Modify Merchants",
              path: "/dashboard/merchantslist",
              id: "merchant-modify",
              icon: <FaListUl />,
            },
            {
              label: "Add Merchant",
              path: "/dashboard/merchant?mode=create",
              id: "merchant-add",
              icon: <FaPlus />,
            },
          ],
        },
        {
          id: "terminal",
          label: "Terminal",
          icon: <FaCreditCard />,
          submenu: [
            {
              label: "Modify Terminals",
              path: "/dashboard/terminalslist",
              id: "terminal-modify",
              icon: <FaListUl />,
            },
            {
              label: "Add Terminal",
              path: "/dashboard/terminal?mode=create",
              id: "terminal-add",
              icon: <FaPlus />,
            },

            {
              label: "Unbind Terminal",
              path: "/dashboard/unbind",
              id: "terminal-unbind",
              icon: <FaUnlink />,
            },
            {
              label: "Reset Password",
              path: "/dashboard/operations",
              id: "terminal-reset",
              icon: <FaKey />,
            },
          ],
        },
        {
          id: "hardpos",
          label: "Hard POS",
          icon: <FaDesktop />,
          submenu: [
            {
              label: "Modify Hard POS",
              path: "/dashboard/hardpos",
              id: "hardpos-modify",
              icon: <FaListUl />,
            },
            {
              label: "Add Hard POS",
              path: "/dashboard/hardpos?mode=add",
              id: "hardpos-add",
              icon: <FaPlus />,
            },
          ],
        },
        {
          id: "inventory",
          label: "Inventory",
          icon: <FaWarehouse />,
          path: "/dashboard/inventory",
        },
        // {
        //   id: "unbind",
        //   label: "Unbind Terminal",
        //   icon: <FaUnlink />,
        //   path: "/dashboard/unbind",
        // },
        // {
        //   id: "operations",
        //   label: "Operations",
        //   icon: <FaCogs />,
        //   path: "/dashboard/operations",
        // },
      ],
    },
    {
      id: "analytics",
      label: "ANALYTICS",
      items: [
        {
          id: "reporting",
          label: "Reporting",
          icon: <FaChartBar />,
          submenu: [
            {
              label: "Total",
              path: "/dashboard/reporting?type=transactions&status=Total",
              id: "report-total",
              icon: <FaMoneyBillWave />,
            },
            {
              label: "Settled",
              path: "/dashboard/reporting?type=transactions&status=Settled",
              id: "report-settled",
              icon: <FaCheckCircle />,
            },
            {
              label: "Un-Settled",
              path: "/dashboard/reporting?type=transactions&status=Un-Settled",
              id: "report-unsettled",
              icon: <FaClock />,
            },
            {
              label: "Disputed",
              path: "/dashboard/reporting?type=transactions&status=Disputed",
              id: "report-disputed",
              icon: <FaExclamationTriangle />,
            },
            {
              label: "Declined",
              path: "/dashboard/reporting?type=transactions&status=Declined",
              id: "report-declined",
              icon: <FaTimesCircle />,
            },
            {
              label: "Voided",
              path: "/dashboard/reporting?type=transactions&status=Voided",
              id: "report-voided",
              icon: <FaBan />,
            },
            {
              label: "Today's",
              path: "/dashboard/reporting?type=transactions&status=Today",
              id: "report-today",
              icon: <FaCalendarDay />,
            },

            // {
            //   label: "Terminal Wise Report",
            //   path: "/dashboard/reporting?type=terminal-wise",
            //   id: "report-terminal-wise",
            //   icon: <FaCreditCard />,
            // },
            // {
            //   label: "Merchant Wise Report",
            //   path: "/dashboard/reporting?type=merchant-wise",
            //   id: "report-merchant-wise",
            //   icon: <FaStore />,
            // },
            // {
            //   label: "City Wise Report",
            //   path: "/dashboard/reporting?type=city-wise",
            //   id: "report-city-wise",
            //   icon: <FaCity />,
            // },
            // {
            //   label: "Region Wise Report",
            //   path: "/dashboard/reporting?type=province-wise",
            //   id: "report-province-wise",
            //   icon: <FaMapMarkedAlt />,
            // },
            // {
            //   label: "MCC Wise Report",
            //   path: "/dashboard/reporting?type=mcc-wise",
            //   id: "report-mcc-wise",
            //   icon: <FaTags />,
            // },

            // {
            //   label: "Daily Volume Report",
            //   path: "/dashboard/reporting?type=dvr",
            //   id: "report-dvr",
            //   icon: <FaChartLine />,
            // },
          ],
        },
      ],
    },

    {
      id: "user-management",
      label: "USER MANAGEMENT",
      items: [
        {
          id: "users",
          label: "User Management",
          icon: <FaUser />,
          submenu: [
            {
              label: "Modify User",
              path: "/dashboard/userslist",
              id: "user-modify",
              icon: <FaUserEdit />,
            },
            {
              label: "Add User",
              path: "/dashboard/user?mode=create",
              id: "user-add",
              icon: <FaUserPlus />,
            },
          ],
        },
      ],
    },
  ];

  const handleNavigate = (path) => {
    navigate(path);
  };

  const renderMenuItem = (item) => {
    if (!visibleItems.includes(item.id)) return null;

    return (
      <li key={item.id} className="menu-item">
        {item.submenu ? (
          <>
            <button
              className={`menu-button ${expandedMenus[item.id] ? "expanded" : ""} ${
                item.submenu.some((sub) => isExactActive(sub.path))
                  ? "active-parent"
                  : ""
              }`}
              onClick={() => toggleMenu(item.id)}
            >
              <span className="menu-icon">{item.icon}</span>
              <span className="menu-label">{item.label}</span>
              <span className="menu-chevron">
                {expandedMenus[item.id] ? <FaChevronUp /> : <FaChevronDown />}
              </span>
            </button>

            {expandedMenus[item.id] && (
              <ul className="submenu">
                {item.submenu.map((subitem) => (
                  <li key={subitem.id} className="submenu-item">
                    <button
                      className={`submenu-button ${isExactActive(subitem.path) ? "active" : ""}`}
                      onClick={() => handleNavigate(subitem.path)}
                    >
                      <span className="submenu-icon">{subitem.icon}</span>
                      <span>{subitem.label}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        ) : (
          <button
            className={`menu-button ${isPathActive(item.path) ? "active" : ""}`}
            onClick={() => handleNavigate(item.path)}
          >
            <span className="menu-icon">{item.icon}</span>
            <span className="menu-label">{item.label}</span>
          </button>
        )}
      </li>
    );
  };

  return (
    <div className="sidebar">
      <div className="sidebar-top">
        <div className="sidebar-logo">
          <img src={mindscraft} alt="App Logo" />
        </div>

        <nav className="sidebar-nav">
          {sections.map((section) => {
            const visibleSectionItems = section.items.filter((item) =>
              visibleItems.includes(item.id),
            );
            if (visibleSectionItems.length === 0) return null;

            return (
              <div key={section.id} className="nav-section">
                {section.label && (
                  <span className="section-heading">{section.label}</span>
                )}
                <ul className="sidebar-menu">
                  {visibleSectionItems.map(renderMenuItem)}
                </ul>
              </div>
            );
          })}
        </nav>
      </div>

      <div className="sidebar-bottom">
        <div className="sidebar-footer">
          <div className="powered-by">
            <span>Powered by</span>
            <img src={mindscraft} alt="Mindscraft" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
